import { useCallback, useEffect, useRef, useState } from 'react';
import OtterService from '../services/OtterService';
import { wearItem, indexCatalog, conflictNote } from '../utils/otterWardrobe';

/**
 * Neue Freischaltungen nachholen und zeigen (Aura v2, Okt 2026).
 *
 * Bis dahin gab es einen Level-Aufstieg nur zu sehen, wenn im richtigen Moment
 * die WebSocket-Verbindung offen war. Jetzt fragt die App selbst nach, was neu
 * ist (GET /otter/unseen):
 *  - beim Start (sobald jemand eingeloggt ist),
 *  - nach einem Reconnect der Benachrichtigungen und nach AURA_UPDATE mit
 *    data.kind 'UNLOCK' (beides ueber `refreshSignal`, kurz entprellt, weil das
 *    Backend jedes Teil einzeln meldet).
 *
 * LEVEL-Eintraege loesen die vorhandene LevelUpCelebration aus (`onLevel`): das
 * hoechste ungesehene Level einmal, alle als gesehen markiert. Hat die
 * WebSocket-Meldung das Level schon gezeigt (`celebratedLevel`), wird nur noch
 * als gesehen markiert (keine Doppelanzeige).
 *
 * Teile (ACHIEVEMENT) landen im Sheet "Neu im Schrank". Was gerade gezeigt wird,
 * ist eingefroren (`sheet.items`); spaeter eintreffende Teile kommen danach.
 * Schliessen markiert als gesehen. Waehrend Session, Fokus, Chat und Scanner
 * (`quiet === false`) wartet beides.
 *
 * Ist der Schrank der Schule aus (enabled false), passiert nichts.
 */
export const UNLOCK_REFRESH_DEBOUNCE_MS = 300;

const LEVEL_PREFIX = 'level_';
const levelFromKey = (key) => Number.parseInt(String(key).slice(LEVEL_PREFIX.length), 10);
const levelKeysUpTo = (n) => {
  const keys = [];
  for (let i = 2; i <= n; i += 1) keys.push(`${LEVEL_PREFIX}${i}`);
  return keys;
};

const quietly = (promise) => Promise.resolve(promise).catch(() => {});

export default function useUnlocks({
  userId,
  quiet = true,
  refreshSignal = 0,
  celebratedLevel = null,
  levelUpOpen = false,
  onLevel,
} = {}) {
  const [enabled, setEnabled] = useState(false);
  const [wardrobe, setWardrobe] = useState(null);
  const [pending, setPending] = useState([]);
  const [pendingLevel, setPendingLevel] = useState(null);
  const [sheet, setSheet] = useState(null);

  const generationRef = useRef(0);
  const shownLevelRef = useRef(0);
  const enabledRef = useRef(false);
  const wardrobeRef = useRef(null);
  wardrobeRef.current = wardrobe;
  const onLevelRef = useRef(onLevel);
  onLevelRef.current = onLevel;

  // Anderes Konto (oder Logout): alles vergessen, laufende Antworten verwerfen
  useEffect(() => {
    generationRef.current += 1;
    shownLevelRef.current = 0;
    enabledRef.current = false;
    setEnabled(false);
    setWardrobe(null);
    setPending([]);
    setPendingLevel(null);
    setSheet(null);
  }, [userId]);

  const load = useCallback(async () => {
    if (!userId) return;
    const generation = generationRef.current;
    let data;
    try {
      data = await OtterService.getUnseen();
    } catch {
      return; // kein Netz, Serverfehler: beim naechsten Anlass
    }
    if (generation !== generationRef.current) return;
    if (!data || data.enabled !== true) {
      enabledRef.current = false;
      setEnabled(false);
      return;
    }
    enabledRef.current = true;
    setEnabled(true);

    const rows = Array.isArray(data.unlocked) ? data.unlocked : [];
    const levelRows = rows.filter((row) => row && row.kind === 'LEVEL');
    const partRows = rows.filter((row) => row && row.kind === 'ACHIEVEMENT');

    if (levelRows.length > 0) {
      const keys = levelRows.map((row) => row.key);
      const top = Math.max(0, ...keys.map(levelFromKey).filter(Number.isFinite));
      if (top > shownLevelRef.current) {
        setPendingLevel((prev) => ({
          newLevel: Math.max(top, prev?.newLevel || 0),
          keys: [...new Set([...(prev?.keys || []), ...keys])],
        }));
      } else {
        // Schon gezeigt (WebSocket-Meldung): nur noch als gesehen markieren
        quietly(OtterService.markSeen(keys));
      }
    }

    if (partRows.length > 0) {
      let next;
      try {
        next = await OtterService.getWardrobe();
      } catch {
        return; // ohne Katalog kein Sheet, beim naechsten Anlass
      }
      if (generation !== generationRef.current || !next || next.enabled !== true) return;
      setWardrobe(next);
      setPending((prev) => {
        const have = new Set(prev.map((row) => row.key));
        return [...prev, ...partRows.filter((row) => !have.has(row.key))];
      });
    }
  }, [userId]);

  // App-Start / Login
  useEffect(() => {
    if (userId) load();
  }, [userId, load]);

  // Reconnect bzw. Freischaltung per WebSocket (der Startwert zaehlt nicht)
  const firstSignalRef = useRef(true);
  useEffect(() => {
    if (firstSignalRef.current) {
      firstSignalRef.current = false;
      return undefined;
    }
    if (!userId) return undefined;
    const timer = setTimeout(load, UNLOCK_REFRESH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [refreshSignal, userId, load]);

  // Level-Up kam schon per WebSocket: gesehen markieren statt noch mal zeigen
  useEffect(() => {
    const n = Number.parseInt(celebratedLevel, 10);
    if (!Number.isFinite(n) || n < 2) return;
    shownLevelRef.current = Math.max(shownLevelRef.current, n);
    if (enabledRef.current) quietly(OtterService.markSeen(levelKeysUpTo(n)));
    setPendingLevel((prev) => {
      if (prev && prev.newLevel <= n) {
        quietly(OtterService.markSeen(prev.keys));
        return null;
      }
      return prev;
    });
  }, [celebratedLevel]);

  // Level zeigen, sobald es ruhig ist
  useEffect(() => {
    if (!pendingLevel || !quiet || levelUpOpen) return;
    shownLevelRef.current = Math.max(shownLevelRef.current, pendingLevel.newLevel);
    if (typeof onLevelRef.current === 'function') {
      onLevelRef.current({ oldLevel: null, newLevel: pendingLevel.newLevel });
    }
    quietly(OtterService.markSeen(pendingLevel.keys));
    setPendingLevel(null);
  }, [pendingLevel, quiet, levelUpOpen]);

  // Sheet oeffnen: nach einem Level, nur in ruhigen Momenten, Inhalt einfrieren
  useEffect(() => {
    if (sheet || !quiet || levelUpOpen || pendingLevel || pending.length === 0 || !wardrobe) return;
    const byKey = indexCatalog(wardrobe.catalog);
    const items = pending
      .map((row) => byKey[row.key])
      .filter(Boolean);
    const unknown = pending.filter((row) => !byKey[row.key]).map((row) => row.key);
    if (unknown.length) quietly(OtterService.markSeen(unknown));
    if (items.length === 0) {
      setPending([]);
      return;
    }
    // Erstes Teil ueberhaupt = noch nie ein Teil mit Accessory gesehen
    const seenBefore = (wardrobe.unlocked || []).some((row) => (
      row.kind === 'ACHIEVEMENT' && row.seenAt && byKey[row.key]?.accessory
    ));
    const hasPart = items.some((item) => item.accessory);
    setSheet({
      items,
      askVisibility: hasPart && !seenBefore && wardrobe.otterVisible === null,
    });
  }, [sheet, quiet, levelUpOpen, pendingLevel, pending, wardrobe]);

  const sheetRef = useRef(null);
  sheetRef.current = sheet;
  const closeSheet = useCallback(() => {
    const current = sheetRef.current;
    if (!current) return;
    const keys = current.items.map((item) => item.key);
    quietly(OtterService.markSeen(keys));
    setPending((prev) => prev.filter((row) => !keys.includes(row.key)));
    setSheet(null);
  }, []);

  // Anziehen aus dem Sheet: optimistisch, bei Fehler zurueck
  const wear = useCallback(async (key) => {
    const current = wardrobeRef.current;
    if (!current) return { ok: false, error: 'Das hat gerade nicht geklappt.' };
    const before = current.outfit || {};
    const { outfit, removed, action } = wearItem(before, key, current.catalog);
    if (action === 'none') return { ok: false, error: 'Dieses Teil passt nicht an deinen Otter.' };
    setWardrobe((prev) => (prev ? { ...prev, outfit } : prev));
    try {
      const saved = await OtterService.saveOutfit(outfit);
      if (saved && saved.outfit) setWardrobe((prev) => (prev ? { ...prev, outfit: saved.outfit } : prev));
      return { ok: true, note: conflictNote(key, removed, current.catalog) };
    } catch {
      setWardrobe((prev) => (prev ? { ...prev, outfit: before } : prev));
      return { ok: false, error: 'Anziehen hat gerade nicht geklappt. Im Schrank kannst du es noch mal versuchen.' };
    }
  }, []);

  const setVisibility = useCallback(async (visible) => {
    try {
      await OtterService.setVisibility(visible);
      setWardrobe((prev) => (prev ? { ...prev, otterVisible: Boolean(visible) } : prev));
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    enabled,
    wardrobe,
    sheet,
    wear,
    setVisibility,
    closeSheet,
    reload: load,
  };
}
